import { useCallback, useState } from "react";

/**
 * Overlay/panel UI state for the Classification Review page.
 *
 * These three pieces of state drive the stacked overlays:
 *   - detailImageId   — the open ReviewDetailPanel (null = closed)
 *   - openEventUuid    — the EventPanel stacked above the detail panel
 *   - walkthroughIds   — the ReviewCarousel walkthrough (frozen id snapshot)
 *
 * They are independent of the filter/query state, so they live in their own
 * hook. All handlers are referentially stable so consumers can pass them to
 * memoized children without breaking memoization.
 */
export const useReviewPanels = () => {
  const [detailImageId, setDetailImageId] = useState<number | null>(null);
  // Carousel walkthrough state: null = closed, array = open on these ids.
  // Snapshot is frozen at the moment the user clicks Walk through — further
  // grid changes don't affect the in-progress walkthrough.
  const [walkthroughIds, setWalkthroughIds] = useState<number[] | null>(null);
  // Event panel state — opened from the detail panel's "View event →" button.
  // Stacks above the detail panel (z-50), so closing it brings the user back
  // to the same image they were reviewing.
  const [openEventUuid, setOpenEventUuid] = useState<string | null>(null);

  const openDetail = useCallback((imageId: number) => {
    setDetailImageId(imageId);
  }, []);
  const closeDetail = useCallback(() => setDetailImageId(null), []);

  const openWalkthrough = useCallback((ids: number[]) => {
    setWalkthroughIds(ids);
  }, []);
  const closeWalkthrough = useCallback(() => setWalkthroughIds(null), []);

  const openEvent = useCallback((uuid: string) => setOpenEventUuid(uuid), []);
  const closeEvent = useCallback(() => setOpenEventUuid(null), []);

  /** From the event panel: jump to an image in the detail panel and close the event. */
  const openInDetailFromEvent = useCallback((imageId: number) => {
    setDetailImageId(imageId);
    setOpenEventUuid(null);
  }, []);

  return {
    detailImageId,
    walkthroughIds,
    openEventUuid,
    openDetail,
    closeDetail,
    openWalkthrough,
    closeWalkthrough,
    openEvent,
    closeEvent,
    openInDetailFromEvent,
  };
};
